import { Vector3 } from 'three'
import { SplineType } from '../enums'

/**
 * A faithful-enough port of `Dreamteck.Splines.Spline` (TypeDefIndex 12260) —
 * only the parts the game actually uses.
 *
 * Two things matter for matching the original and are easy to get wrong:
 *
 *  1. **Percent is not arc length.** Dreamteck's `percent` is uniform in
 *     *segment* space: `percent * (pointCount - 1)` indexes the segment, and
 *     the fractional part is the local `t`. Long segments therefore sweep more
 *     world distance per unit percent than short ones. Everything the game
 *     stores about a bus's position on the road is in this percent space.
 *
 *  2. **BSpline does not interpolate its control points.** A uniform cubic
 *     B-spline passes near, not through, the nodes — which is exactly why
 *     `SplineGenerator.Initialize` has to `Project()` the first and last
 *     authored node back onto the curve to find where the visible road starts
 *     and ends, instead of just using percent 0 and 1.
 *
 * NOTE: `CalculateBSplinePosition` is not in the decompiled set (Ghidra only
 * covered game code, not the Dreamteck plugin), so the basis below is the
 * standard uniform cubic B-spline. Verify against the reference clip during
 * tuning — see docs/PORTING.md.
 */
export class DreamteckSpline {
  readonly points: Vector3[]
  readonly type: SplineType
  readonly closed: boolean
  /** Dreamteck's `SplineComputer.sampleRate`; its default is 10. */
  readonly sampleRate: number

  /** Cumulative chord length at each LUT sample. */
  private _lutDistance: Float64Array
  /** Percent at each LUT sample. */
  private _lutPercent: Float64Array
  private _lutPosition: Vector3[]
  private _length = 0

  constructor(points: Vector3[], type: SplineType, closed = false, sampleRate = 10) {
    if (points.length < 2) throw new Error('DreamteckSpline needs at least 2 points')
    this.points = points
    this.type = type
    this.closed = closed
    this.sampleRate = sampleRate
    this._lutDistance = new Float64Array(0)
    this._lutPercent = new Float64Array(0)
    this._lutPosition = []
    this.rebuild()
  }

  /** Number of curve segments. Matches Dreamteck's `iterations - 1`. */
  get segmentCount(): number {
    return this.closed ? this.points.length : this.points.length - 1
  }

  get length(): number {
    return this._length
  }

  // -------------------------------------------------------------------------
  // Evaluation
  // -------------------------------------------------------------------------

  /** Control point at `i`, clamped (open) or wrapped (closed) as Dreamteck does. */
  private point(i: number): Vector3 {
    const n = this.points.length
    if (this.closed) return this.points[((i % n) + n) % n]
    return this.points[i < 0 ? 0 : i >= n ? n - 1 : i]
  }

  /** Split a percent into (segment index, local t). */
  private locate(percent: number): { i: number; t: number } {
    const p = percent <= 0 ? 0 : percent >= 1 ? 1 : percent
    const segments = this.segmentCount
    const scaled = p * segments
    let i = Math.floor(scaled)
    if (i >= segments) i = segments - 1
    return { i, t: scaled - i }
  }

  evaluatePosition(percent: number, out = new Vector3()): Vector3 {
    const { i, t } = this.locate(percent)
    switch (this.type) {
      case SplineType.Linear:
        return this.linearPosition(i, t, out)
      case SplineType.CatmullRom:
        return this.catmullRomPosition(i, t, out)
      case SplineType.BSpline:
      default:
        return this.bSplinePosition(i, t, out)
    }
  }

  evaluateTangent(percent: number, out = new Vector3()): Vector3 {
    const { i, t } = this.locate(percent)
    switch (this.type) {
      case SplineType.Linear:
        out.copy(this.point(i + 1)).sub(this.point(i))
        break
      case SplineType.CatmullRom:
        this.catmullRomTangent(i, t, out)
        break
      case SplineType.BSpline:
      default:
        this.bSplineTangent(i, t, out)
        break
    }
    if (out.lengthSq() < 1e-12) {
      // Degenerate segment — fall back to a finite difference.
      const eps = 1e-4
      const a = this.evaluatePosition(Math.max(0, percent - eps))
      const b = this.evaluatePosition(Math.min(1, percent + eps))
      out.copy(b).sub(a)
    }
    return out.normalize()
  }

  private linearPosition(i: number, t: number, out: Vector3): Vector3 {
    return out.copy(this.point(i)).lerp(this.point(i + 1), t)
  }

  /** Uniform cubic B-spline basis, /6. */
  private bSplinePosition(i: number, t: number, out: Vector3): Vector3 {
    const p0 = this.point(i - 1)
    const p1 = this.point(i)
    const p2 = this.point(i + 1)
    const p3 = this.point(i + 2)
    const t2 = t * t
    const t3 = t2 * t
    const it = 1 - t
    const b0 = (it * it * it) / 6
    const b1 = (3 * t3 - 6 * t2 + 4) / 6
    const b2 = (-3 * t3 + 3 * t2 + 3 * t + 1) / 6
    const b3 = t3 / 6
    return out.set(
      p0.x * b0 + p1.x * b1 + p2.x * b2 + p3.x * b3,
      p0.y * b0 + p1.y * b1 + p2.y * b2 + p3.y * b3,
      p0.z * b0 + p1.z * b1 + p2.z * b2 + p3.z * b3,
    )
  }

  private bSplineTangent(i: number, t: number, out: Vector3): Vector3 {
    const p0 = this.point(i - 1)
    const p1 = this.point(i)
    const p2 = this.point(i + 1)
    const p3 = this.point(i + 2)
    const t2 = t * t
    const it = 1 - t
    const d0 = -(it * it) / 2
    const d1 = (9 * t2 - 12 * t) / 6
    const d2 = (-9 * t2 + 6 * t + 3) / 6
    const d3 = t2 / 2
    return out.set(
      p0.x * d0 + p1.x * d1 + p2.x * d2 + p3.x * d3,
      p0.y * d0 + p1.y * d1 + p2.y * d2 + p3.y * d3,
      p0.z * d0 + p1.z * d1 + p2.z * d2 + p3.z * d3,
    )
  }

  private catmullRomPosition(i: number, t: number, out: Vector3): Vector3 {
    const p0 = this.point(i - 1)
    const p1 = this.point(i)
    const p2 = this.point(i + 1)
    const p3 = this.point(i + 2)
    const t2 = t * t
    const t3 = t2 * t
    const b0 = 0.5 * (-t3 + 2 * t2 - t)
    const b1 = 0.5 * (3 * t3 - 5 * t2 + 2)
    const b2 = 0.5 * (-3 * t3 + 4 * t2 + t)
    const b3 = 0.5 * (t3 - t2)
    return out.set(
      p0.x * b0 + p1.x * b1 + p2.x * b2 + p3.x * b3,
      p0.y * b0 + p1.y * b1 + p2.y * b2 + p3.y * b3,
      p0.z * b0 + p1.z * b1 + p2.z * b2 + p3.z * b3,
    )
  }

  private catmullRomTangent(i: number, t: number, out: Vector3): Vector3 {
    const p0 = this.point(i - 1)
    const p1 = this.point(i)
    const p2 = this.point(i + 1)
    const p3 = this.point(i + 2)
    const t2 = t * t
    const d0 = 0.5 * (-3 * t2 + 4 * t - 1)
    const d1 = 0.5 * (9 * t2 - 10 * t)
    const d2 = 0.5 * (-9 * t2 + 8 * t + 1)
    const d3 = 0.5 * (3 * t2 - 2 * t)
    return out.set(
      p0.x * d0 + p1.x * d1 + p2.x * d2 + p3.x * d3,
      p0.y * d0 + p1.y * d1 + p2.y * d2 + p3.y * d3,
      p0.z * d0 + p1.z * d1 + p2.z * d2 + p3.z * d3,
    )
  }

  // -------------------------------------------------------------------------
  // Arc length
  // -------------------------------------------------------------------------

  /**
   * Rebuild the arc-length LUT. Sample density mirrors Dreamteck's
   * `iterations` (`sampleRate` samples per segment), so `CalculateLength` here
   * lands on the same chord-sum approximation the original used — which
   * matters because `SmoothSplineCorners` derives its resample count from it.
   */
  rebuild(): void {
    const count = this.sampleRate * this.segmentCount + 1
    const distance = new Float64Array(count)
    const percent = new Float64Array(count)
    const position: Vector3[] = new Array(count)

    const step = 1 / (count - 1)
    let total = 0
    for (let k = 0; k < count; k++) {
      const p = k === count - 1 ? 1 : k * step
      const pos = this.evaluatePosition(p)
      if (k > 0) total += pos.distanceTo(position[k - 1])
      percent[k] = p
      position[k] = pos
      distance[k] = total
    }

    this._lutDistance = distance
    this._lutPercent = percent
    this._lutPosition = position
    this._length = total
  }

  /** `Spline.CalculateLength(from, to)`. */
  calculateLength(from = 0, to = 1): number {
    if (from <= 0 && to >= 1) return this._length
    return this.distanceAt(to) - this.distanceAt(from)
  }

  /** Arc length from percent 0 up to `percent`. */
  distanceAt(percent: number): number {
    const p = percent <= 0 ? 0 : percent >= 1 ? 1 : percent
    const lut = this._lutPercent
    const k = this.lutIndexForPercent(p)
    if (k >= lut.length - 1) return this._lutDistance[lut.length - 1]
    const span = lut[k + 1] - lut[k]
    const f = span > 0 ? (p - lut[k]) / span : 0
    return this._lutDistance[k] + (this._lutDistance[k + 1] - this._lutDistance[k]) * f
  }

  private lutIndexForPercent(p: number): number {
    // Samples are uniform in percent, so the index is direct.
    const k = Math.floor(p * (this._lutPercent.length - 1))
    return k < 0 ? 0 : k >= this._lutPercent.length - 1 ? this._lutPercent.length - 2 : k
  }

  /**
   * `Spline.Travel(start, distance, direction)` — walk `distance` world units
   * along the curve from `start` and return the percent you land on.
   */
  travel(startPercent: number, distance: number, forward = true): number {
    const target = this.distanceAt(startPercent) + (forward ? distance : -distance)
    return this.percentAtDistance(target)
  }

  /** Inverse of `distanceAt`. */
  percentAtDistance(distance: number): number {
    const d = this._lutDistance
    const last = d.length - 1
    if (distance <= 0) return 0
    if (distance >= d[last]) return 1

    let lo = 0
    let hi = last
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1
      if (d[mid] <= distance) lo = mid
      else hi = mid
    }
    const span = d[hi] - d[lo]
    const f = span > 0 ? (distance - d[lo]) / span : 0
    return this._lutPercent[lo] + (this._lutPercent[hi] - this._lutPercent[lo]) * f
  }

  /**
   * `Spline.Project(position)` — the percent whose point is closest to
   * `position`. Coarse LUT scan then a local refinement, same shape as
   * Dreamteck's subdivide-and-narrow.
   */
  project(position: Vector3, refineIterations = 4): number {
    const lut = this._lutPosition
    let best = 0
    let bestSq = Infinity
    for (let k = 0; k < lut.length; k++) {
      const dsq = lut[k].distanceToSquared(position)
      if (dsq < bestSq) {
        bestSq = dsq
        best = k
      }
    }

    const step = 1 / (lut.length - 1)
    let lo = Math.max(0, (best - 1) * step)
    let hi = Math.min(1, (best + 1) * step)
    let bestPercent = best * step
    const probe = new Vector3()
    for (let it = 0; it < refineIterations; it++) {
      const slices = 10
      const width = (hi - lo) / slices
      for (let s = 0; s <= slices; s++) {
        const p = lo + width * s
        const dsq = this.evaluatePosition(p, probe).distanceToSquared(position)
        if (dsq < bestSq) {
          bestSq = dsq
          bestPercent = p
        }
      }
      lo = Math.max(0, bestPercent - width)
      hi = Math.min(1, bestPercent + width)
    }
    return bestPercent
  }

  /** Positions of the LUT samples — handy for building road geometry. */
  get samplePositions(): readonly Vector3[] {
    return this._lutPosition
  }
}
